import { useCallback, useEffect, useState } from 'react';
import { listSnapshots, deleteSnapshot, spawnFromSnapshot } from '../services/api';
import type { OrganismSnapshot } from '../types/simulation';
import { useNotification } from '../contexts/NotificationContext';
import './SnapshotBrowser.css';

interface SnapshotBrowserProps {
  simulationId: string;
  tabMode?: boolean;
}

function formatDate(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
}

export function SnapshotBrowser({ simulationId, tabMode = false }: SnapshotBrowserProps) {
  const { showNotification } = useNotification();
  const [snapshots, setSnapshots] = useState<OrganismSnapshot[]>([]);
  const [collapsed, setCollapsed] = useState(false);
  const [mutateFlags, setMutateFlags] = useState<Record<string, boolean>>({});
  const [spawningIds, setSpawningIds] = useState<Set<string>>(new Set());
  const [deletingIds, setDeletingIds] = useState<Set<string>>(new Set());

  const refresh = useCallback(async () => {
    try {
      const data = await listSnapshots(simulationId);
      setSnapshots(data);
    } catch {
      // non-fatal — sim may be offline
    }
  }, [simulationId]);

  useEffect(() => {
    refresh();
    const id = setInterval(refresh, 5000);
    return () => clearInterval(id);
  }, [refresh]);

  const handleSpawn = async (snap: OrganismSnapshot) => {
    const mutate = mutateFlags[snap.snapshot_id] ?? false;
    setSpawningIds(s => new Set(s).add(snap.snapshot_id));
    try {
      const result = await spawnFromSnapshot(simulationId, snap.snapshot_id, mutate);
      const label = result.substrate_inherited ? 'substrate inherited' : 'fresh connectome';
      showNotification(`Spawned${result.mutated ? ' (mutated)' : ''} · ${label}`);
    } catch (err) {
      showNotification(err instanceof Error ? err.message : 'Spawn failed');
    } finally {
      setSpawningIds(s => { const n = new Set(s); n.delete(snap.snapshot_id); return n; });
    }
  };

  const handleDelete = async (snap: OrganismSnapshot) => {
    if (!confirm(`Delete snapshot "${snap.name || snap.snapshot_id.substring(0, 8)}"?`)) return;
    setDeletingIds(s => new Set(s).add(snap.snapshot_id));
    try {
      await deleteSnapshot(simulationId, snap.snapshot_id);
      setSnapshots(s => s.filter(x => x.snapshot_id !== snap.snapshot_id));
    } catch (err) {
      showNotification(err instanceof Error ? err.message : 'Delete failed');
    } finally {
      setDeletingIds(s => { const n = new Set(s); n.delete(snap.snapshot_id); return n; });
    }
  };

  const toggleMutate = (snapshotId: string) => {
    setMutateFlags(f => ({ ...f, [snapshotId]: !f[snapshotId] }));
  };

  return (
    <div className={`snapshot-browser${collapsed ? ' snapshot-browser--collapsed' : ''}`}>
      {!tabMode && (
        <div className="snapshot-browser-header" onClick={() => setCollapsed(c => !c)}>
          <h3>Snapshots ({snapshots.length})</h3>
          <div className="snapshot-header-controls">
            <button
              className="refresh-btn"
              onClick={e => { e.stopPropagation(); refresh(); }}
              title="Refresh snapshots"
            >
              ↻
            </button>
            <span className={`organism-list-chevron${collapsed ? '' : ' open'}`}>▸</span>
          </div>
        </div>
      )}

      {(!collapsed || tabMode) && (
        <div className="snapshot-list">
          {snapshots.length === 0 && (
            <div className="no-snapshots">No snapshots yet</div>
          )}
          {snapshots.map(snap => {
            const mutate = mutateFlags[snap.snapshot_id] ?? false;
            const spawning = spawningIds.has(snap.snapshot_id);
            const deleting = deletingIds.has(snap.snapshot_id);
            return (
              <div key={snap.snapshot_id} className="snapshot-item">
                <div className="snapshot-meta">
                  <span className="snapshot-name" title={snap.snapshot_id}>
                    {snap.name || snap.organism_id.substring(0, 8)}
                  </span>
                  <span className="snapshot-tags">
                    <span className="snapshot-tag">{snap.body_type}</span>
                    {snap.substrate_id && <span className="snapshot-tag snapshot-tag--substrate">substrate</span>}
                  </span>
                </div>
                <div className="snapshot-date">{formatDate(snap.created_at)}</div>
                <div className="snapshot-actions">
                  <label className="mutate-toggle" title="Mutate genome on spawn">
                    <input
                      type="checkbox"
                      checked={mutate}
                      onChange={() => toggleMutate(snap.snapshot_id)}
                    />
                    mutate
                  </label>
                  <button
                    className="snapshot-spawn-btn"
                    onClick={() => handleSpawn(snap)}
                    disabled={spawning}
                    title="Spawn from snapshot"
                  >
                    {spawning ? '…' : '+ spawn'}
                  </button>
                  <button
                    className="snapshot-delete-btn"
                    onClick={() => handleDelete(snap)}
                    disabled={deleting}
                    title="Delete snapshot"
                  >
                    ✕
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
